import { useEffect, useMemo, useState } from 'react'
import { Button, Card, DataTable, ErrorState, Input, LoadingState, Modal, PageHeader, Switch } from '@/components/ui'
import apiClient from '@/lib/api.ts'

interface CustomerUser {
  id: string
  email: string
  name: string
  role: string
  portalKind: string
  customerId: string | null
  tanssId?: string | null
  isActive: boolean
  lastLoginAt: string | null
}

interface CustomerModuleDef {
  id: string
  label: string
  hint: string
}

// Acht granulare Kundenportal-Berechtigungen. Jeder Eintrag entspricht einem
// Modul-Override in `user_module_permissions` (true = erlaubt, false = verboten).
// Default-DENY (siehe rbac.ts): ohne expliziten Allow-Eintrag bleibt das Modul
// gesperrt. Der Master-Toggle „Alle Optionen im Kundenportal" im UI schaltet
// alle acht gleichzeitig.
const CUSTOMER_MODULES: CustomerModuleDef[] = [
  { id: 'customer-tickets-own', label: 'Eigene Tickets', hint: 'Eigene Tickets sehen, anlegen und kommentieren' },
  { id: 'customer-tickets-all', label: 'Alle Tickets', hint: 'Tickets aller Mitarbeiter der eigenen Firma sehen' },
  { id: 'customer-sap-quotes', label: 'Angebote, Aufträge, Lieferscheine', hint: 'Verkaufsdokumente einsehen' },
  { id: 'customer-sap-invoices', label: 'Rechnungen, Gutschriften', hint: 'Ausgangsrechnungen und Gutschriften einsehen' },
  { id: 'customer-sap-opos', label: 'OPOS', hint: 'Offene Posten / fällige Rechnungen' },
  { id: 'customer-access-matrix', label: 'Zugriffsmatrix', hint: 'Freigaben, User- und Abteilungs-Matrix bearbeiten' },
  { id: 'customer-employees-manage', label: 'Mitarbeiter anlegen/löschen', hint: 'Mitarbeiter ansehen, anlegen, bearbeiten und Löschanträge stellen' },
  { id: 'customer-contracts', label: 'Verträge', hint: 'Verträge und Sicherheitsstatus einsehen' },
]

interface Customer {
  id: string
  name: string
  sapCardCode?: string | null
  tanssId?: string | null
}

interface TanssCompany { id: string; name: string; email: string | null }
interface TanssEmployee { id: string; name: string; email: string | null }

export function CustomerUsersPage() {
  const [users, setUsers] = useState<CustomerUser[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')

  const [editUser, setEditUser] = useState<CustomerUser | null>(null)
  const [editCustomerId, setEditCustomerId] = useState('')
  const [editActive, setEditActive] = useState(false)
  const [editPassword, setEditPassword] = useState('')
  const [editName, setEditName] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [editModules, setEditModules] = useState<Record<string, boolean>>({})
  const [syncingTanss, setSyncingTanss] = useState(false)
  const [editInfo, setEditInfo] = useState<string | null>(null)

  const [editTanssUser, setEditTanssUser] = useState('')
  const [editTanssPassword, setEditTanssPassword] = useState('')
  const [editTanss2faCode, setEditTanss2faCode] = useState('')
  const [editTanssHasPassword, setEditTanssHasPassword] = useState(false)

  const [impersonating, setImpersonating] = useState<string | null>(null)

  // ─── Anlage-Modal ────────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  // Firmen werden live ueber den zentralen SysMan→TANSS-Adapter gesucht
  // (GET /api/tanss/companies/search). Kein lokaler Sync noetig.
  const [companyFilter, setCompanyFilter] = useState('')
  const [companyResults, setCompanyResults] = useState<TanssCompany[] | null>(null)
  const [companySearching, setCompanySearching] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState<TanssCompany | null>(null)

  const [tanssEmployees, setTanssEmployees] = useState<TanssEmployee[] | null>(null)
  const [employeeFilter, setEmployeeFilter] = useState('')
  const [selectedEmployee, setSelectedEmployee] = useState<TanssEmployee | null>(null)


  const [newName, setNewName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [newActive, setNewActive] = useState(true)
  const [newPassword, setNewPassword] = useState('')
  const [newTanssUser, setNewTanssUser] = useState('')
  const [newTanssPassword, setNewTanssPassword] = useState('')
  const [newTanss2fa, setNewTanss2fa] = useState('')

  const load = async () => {
    setLoading(true)
    try {
      const [u, c] = await Promise.all([
        apiClient.get<CustomerUser[]>('/users?portalKind=customer'),
        apiClient.get<Customer[]>('/customers'),
      ])
      setUsers(u.data)
      setCustomers(c.data)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const filteredUsers = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return users
    return users.filter((u) => {
      const cust = customers.find((c) => c.id === u.customerId)?.name ?? ''
      return [u.name, u.email, cust, u.isActive ? 'aktiv' : 'inaktiv'].some((v) => v.toLowerCase().includes(q))
    })
  }, [users, customers, filter])

  const openEdit = async (u: CustomerUser) => {
    setEditUser(u)
    setEditCustomerId(u.customerId ?? '')
    setEditActive(u.isActive)
    setEditPassword('')
    setEditName(u.name)
    setEditEmail(u.email)
    setEditInfo(null)
    setEditTanssUser('')
    setEditTanssPassword('')
    setEditTanss2faCode('')
    setEditTanssHasPassword(false)
    // Default-DENY: ein Modul ist nur aktiv, wenn explizit `allowed=true`
    // in der DB steht. Fehlen Override-Datensaetze (neuer User, neu eingefuehrte
    // Berechtigungen), bleibt der Schalter standardmaessig aus — der Admin
    // muss jedes Recht bewusst freischalten.
    try {
      const res = await apiClient.get<{ overrides: Record<string, boolean>; groupAllowed: Record<string, boolean>; groupIds: string[] }>(`/users/${u.id}/modules`)
      const defaults: Record<string, boolean> = {}
      for (const m of CUSTOMER_MODULES) defaults[m.id] = res.data.overrides[m.id] === true
      setEditModules(defaults)
    } catch {
      const defaults: Record<string, boolean> = {}
      for (const m of CUSTOMER_MODULES) defaults[m.id] = false
      setEditModules(defaults)
    }
    try {
      const creds = await apiClient.get<{ tanssUser: string; tanss2faCode: string; hasPassword: boolean }>(
        `/users/${u.id}/tanss-credentials`,
      )
      setEditTanssUser(creds.data.tanssUser ?? '')
      setEditTanss2faCode(creds.data.tanss2faCode ?? '')
      setEditTanssHasPassword(!!creds.data.hasPassword)
    } catch {
      // Lese-Fehler ist nicht kritisch — Felder bleiben leer.
    }
  }

  const saveEdit = async () => {
    if (!editUser) return
    if (editPassword && editPassword.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen haben (oder leer lassen für keine Änderung)')
      return
    }
    try {
      const body: Record<string, unknown> = {
        customerId: editCustomerId || null,
        isActive: editActive,
      }
      if (editPassword) body.password = editPassword
      if (!editUser.tanssId) {
        if (editName.trim() !== editUser.name) body.name = editName.trim()
        if (editEmail.trim().toLowerCase() !== editUser.email) body.email = editEmail.trim()
      }
      await apiClient.patch(`/users/${editUser.id}`, body)

      // Modul-Overrides separat schreiben – PUT /users/:id/modules
      await apiClient.put(`/users/${editUser.id}/modules`, { modules: editModules })

      // TANSS-Credentials nur schreiben, wenn ein Benutzername gesetzt ist.
      // Passwort ist optional, wenn schon eines gespeichert ist; bei Erstanlage
      // verlangt das Backend ein Passwort.
      if (editTanssUser.trim() && (editTanssPassword || editTanssHasPassword)) {
        await apiClient.put(`/users/${editUser.id}/tanss-credentials`, {
          tanssUser: editTanssUser.trim(),
          tanssPassword: editTanssPassword || undefined,
          tanss2faCode: editTanss2faCode,
        })
      }

      setEditUser(null)
      setEditPassword('')
      setEditTanssUser('')
      setEditTanssPassword('')
      setEditTanss2faCode('')
      setEditTanssHasPassword(false)
      setEditInfo(null)
      await load()
    } catch (e) {
      const ax = e as { response?: { data?: { error?: string; message?: string } } }
      setError(ax.response?.data?.error ?? ax.response?.data?.message ?? (e instanceof Error ? e.message : 'Speichern fehlgeschlagen'))
    }
  }

  const refreshFromTanss = async () => {
    if (!editUser) return
    setSyncingTanss(true)
    setEditInfo(null)
    try {
      const res = await apiClient.post<{ changed: boolean; updates: { name: string; email: string } }>(`/users/${editUser.id}/refresh-from-tanss`, {})
      setEditName(res.data.updates.name)
      setEditEmail(res.data.updates.email)
      setEditInfo(res.data.changed ? 'Aus TANSS aktualisiert' : 'Bereits aktuell')
      await load()
    } catch (e) {
      const ax = e as { response?: { data?: { error?: string; message?: string } } }
      setEditInfo(null)
      setError(ax.response?.data?.error ?? ax.response?.data?.message ?? (e instanceof Error ? e.message : 'Sync fehlgeschlagen'))
    } finally {
      setSyncingTanss(false)
    }
  }

  // ─── Anlage-Flow ─────────────────────────────────────────────────────────
  const resetCreate = () => {
    setCreateError(null)
    setCompanyFilter(''); setCompanyResults(null); setSelectedCompany(null)
    setTanssEmployees(null); setEmployeeFilter(''); setSelectedEmployee(null)
    setNewName(''); setNewEmail(''); setNewActive(true); setNewPassword('')
    setNewTanssUser(''); setNewTanssPassword(''); setNewTanss2fa('')
  }

  const openCreate = () => {
    setShowCreate(true)
    resetCreate()
  }

  // Debounced Live-Suche gegen den zentralen TANSS-Adapter
  useEffect(() => {
    if (!showCreate || selectedCompany) return
    const q = companyFilter.trim()
    if (q.length < 2) { setCompanyResults(null); return }
    const handle = setTimeout(async () => {
      setCompanySearching(true)
      try {
        const res = await apiClient.get<{ data: TanssCompany[] }>('/tanss/companies/search', { params: { q } })
        setCompanyResults(res.data.data)
        setCreateError(null)
      } catch (e) {
        const ax = e as { response?: { data?: { error?: string } } }
        setCreateError(ax.response?.data?.error ?? (e instanceof Error ? e.message : 'Suche fehlgeschlagen'))
        setCompanyResults([])
      } finally {
        setCompanySearching(false)
      }
    }, 300)
    return () => clearTimeout(handle)
  }, [companyFilter, showCreate, selectedCompany])

  const selectCompany = async (c: TanssCompany) => {
    setSelectedCompany(c)
    setSelectedEmployee(null)
    setTanssEmployees(null)
    setEmployeeFilter('')
    try {
      const res = await apiClient.get<{ data: TanssEmployee[] }>(`/tanss/companies/${c.id}/persons`)
      setTanssEmployees(res.data.data)
    } catch (e) {
      const ax = e as { response?: { data?: { error?: string } } }
      setCreateError(ax.response?.data?.error ?? (e instanceof Error ? e.message : 'Ansprechpartner nicht ladbar. Name und E-Mail bitte manuell eintragen.'))
      setTanssEmployees([])
    }
  }

  const selectEmployee = async (e: TanssEmployee) => {
    setSelectedEmployee(e)
    // Der List-Endpoint liefert oft nur id + name (TANSS-Bug: keine E-Mail).
    // Die vollstaendigen Stammdaten (E-Mail, Telefon) stehen erst im
    // Detail-Objekt — daher hier nachladen und die Felder befuellen.
    let detailEmail: string | null = e.email ?? null
    let detailName: string | null = e.name ?? null
    if (selectedCompany) {
      try {
        const res = await apiClient.get<{ data: { name: string | null; email: string | null } }>(
          `/tanss/companies/${selectedCompany.id}/persons/${e.id}`,
        )
        detailEmail = res.data.data.email ?? detailEmail
        detailName = res.data.data.name ?? detailName
      } catch (err) {
        // Detail-Fetch ist optional — wenn er fehlschlaegt, nutzen wir die Listen-Daten
        // und zeigen eine dezente Warnung.
        const ax = err as { response?: { data?: { error?: string } }, message?: string }
        setCreateError(ax.response?.data?.error ?? ax.message ?? 'Person-Details konnten nicht geladen werden — E-Mail bitte manuell eintragen.')
      }
    }
    if (!newName && detailName) setNewName(detailName)
    if (!newEmail && detailEmail) setNewEmail(detailEmail)
  }


  const filteredEmployees = useMemo(() => {
    const list = tanssEmployees ?? []
    const q = employeeFilter.trim().toLowerCase()
    if (!q) return list.slice(0, 50)
    return list.filter((e) => [e.name, e.email ?? '', e.id].some((v) => v.toLowerCase().includes(q))).slice(0, 50)
  }, [tanssEmployees, employeeFilter])

  const createUser = async () => {
    setCreateError(null)
    if (!selectedCompany) { setCreateError('Bitte Kunde auswählen'); return }
    if (!newName.trim() || !newEmail.trim()) { setCreateError('Name und E-Mail sind Pflicht'); return }
    if (newPassword && newPassword.length < 8) { setCreateError('Passwort muss mindestens 8 Zeichen haben (oder leer lassen für M365-Login)'); return }
    setCreating(true)
    try {
      // selectedCompany.id kann die lokale UUID oder die TANSS-ID sein.
      // Lokale UUIDs haben Bindestriche (36 Zeichen) — alles andere ist TANSS.
      const isLocalUuid = /^[0-9a-f-]{36}$/i.test(selectedCompany.id)
      const res = await apiClient.post<{ id: string }>('/users', {
        email: newEmail.trim().toLowerCase(),
        name: newName.trim(),
        role: 'customer',
        portalKind: 'customer',
        isActive: newActive,
        password: newPassword || undefined,
        ...(isLocalUuid
          ? { customerId: selectedCompany.id }
          : { tanssCompanyId: String(selectedCompany.id), tanssCompanyName: selectedCompany.name }),
        tanssContactId: selectedEmployee?.id != null ? String(selectedEmployee.id) : undefined,
      })
      const newId = res.data?.id
      if (newId && newTanssUser && newTanssPassword) {
        try {
          await apiClient.put(`/users/${newId}/tanss-credentials`, {
            tanssUser: newTanssUser,
            tanssPassword: newTanssPassword,
            tanss2faCode: newTanss2fa || '',
          })
        } catch (e) {
          setCreateError(`User angelegt, aber TANSS-Credentials fehlgeschlagen: ${e instanceof Error ? e.message : e}`)
        }
      }
      setShowCreate(false)
      resetCreate()
      await load()
    } catch (e) {
      const ax = e as { response?: { data?: { error?: string; message?: string } } }
      setCreateError(ax.response?.data?.error ?? ax.response?.data?.message ?? (e instanceof Error ? e.message : 'Anlegen fehlgeschlagen'))
    } finally {
      setCreating(false)
    }
  }

  const startImpersonation = async (u: CustomerUser) => {
    setImpersonating(u.id)
    try {
      const res = await apiClient.post<{ redirectUrl: string }>(`/admin/impersonate/${u.id}/start`, {})
      window.open(res.data.redirectUrl, '_blank', 'noopener,noreferrer')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impersonation fehlgeschlagen')
    } finally {
      setImpersonating(null)
    }
  }

  if (loading) return <LoadingState />
  if (error) return <ErrorState details={error} />

  return (
    <div>
      <PageHeader
        title="Kunden-User"
        description="Verwaltung aller Kundenportal-Accounts. Freischaltung, Kundenzuordnung und TANSS-Credentials."
        actions={<Button variant="primary" onClick={openCreate}>Neuer Kunden-User</Button>}
      />

      <Card>
        <Card.Body>
          <div className="mb-3 max-w-sm">
            <Input
              placeholder="Suche nach Name, E-Mail, Kunde …"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <DataTable<CustomerUser>
            data={filteredUsers}
            getRowId={(u) => u.id}
            emptyState={
              <div className="text-sm text-gray-500 py-4 text-center">
                {users.length === 0
                  ? 'Noch keine Kunden-User. Klicke oben rechts auf „Neuer Kunden-User" oder warte auf den ersten Microsoft-Login.'
                  : 'Keine Treffer für deinen Filter.'}
              </div>
            }
            columns={[
              {
                accessorKey: 'name',
                header: 'Name',
              },
              {
                accessorKey: 'email',
                header: 'E-Mail',
              },
              {
                id: 'customer',
                header: 'Kunde',
                cell: ({ row }) => {
                  const customer = customers.find((c) => c.id === row.original.customerId)
                  return customer?.name ?? <span className="text-red-600">–</span>
                },
              },
              {
                accessorKey: 'isActive',
                header: 'Status',
                cell: ({ row }) => (row.original.isActive ? 'Aktiv' : 'Inaktiv'),
              },
              {
                accessorKey: 'lastLoginAt',
                header: 'Letzter Login',
                cell: ({ row }) =>
                  row.original.lastLoginAt ? new Date(row.original.lastLoginAt).toLocaleString('de-DE') : '–',
              },
              {
                id: 'actions',
                header: 'Aktionen',
                enableSorting: false,
                cell: ({ row }) => (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="xs" onClick={() => openEdit(row.original)}>Bearbeiten</Button>
                    <Button
                      variant="ghost"
                      size="xs"
                      disabled={!row.original.isActive || !row.original.customerId || impersonating === row.original.id}
                      onClick={() => startImpersonation(row.original)}
                    >
                      {impersonating === row.original.id ? '…' : 'Impersonate'}
                    </Button>
                  </div>
                ),
              },
            ]}
          />
        </Card.Body>
      </Card>

      <Modal open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null) }} title="Kunden-User bearbeiten" size="lg">
        {editUser && (
          <div className="space-y-4">
            {editInfo && (
              <div className="p-2 rounded bg-green-50 border border-green-200 text-xs text-green-900">{editInfo}</div>
            )}

            {/* Stammdaten */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-gray-700">Stammdaten</div>
                {editUser.tanssId ? (
                  <Button variant="ghost" size="xs" onClick={refreshFromTanss} disabled={syncingTanss}>
                    {syncingTanss ? 'Synchronisiere …' : 'Aus TANSS aktualisieren'}
                  </Button>
                ) : null}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  disabled={!!editUser.tanssId}
                />
                <Input
                  label="E-Mail (Login)"
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  disabled={!!editUser.tanssId}
                />
              </div>
              {editUser.tanssId && (
                <p className="text-xs text-gray-500 mt-1">
                  Dieser User ist mit TANSS verknüpft (ID: {editUser.tanssId}). Name und E-Mail werden aus TANSS gezogen.
                </p>
              )}
            </div>

            {/* Zuordnung */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kunde</label>
              <select
                className="w-full border border-gray-300 rounded px-2 py-1"
                value={editCustomerId}
                onChange={(e) => setEditCustomerId(e.target.value)}
              >
                <option value="">– kein Kunde zugeordnet –</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} {c.sapCardCode ? `(${c.sapCardCode})` : ''}</option>
                ))}
              </select>
            </div>

            <Switch label="Aktiv" checked={editActive} onChange={(e) => setEditActive(e.target.checked)} />

            <Input
              label="Passwort zurücksetzen (optional)"
              type="password"
              value={editPassword}
              onChange={(e) => setEditPassword(e.target.value)}
              hint="Leer lassen für keine Änderung. Mindestens 8 Zeichen, wenn gesetzt."
            />

            {/* Modul-Rechte */}
            <div className="border-t border-gray-200 pt-3">
              <div className="text-sm font-medium text-gray-700 mb-2">Modul-Rechte</div>
              {(() => {
                const allOn = CUSTOMER_MODULES.every((m) => editModules[m.id] ?? true)
                const noneOn = CUSTOMER_MODULES.every((m) => (editModules[m.id] ?? true) === false)
                return (
                  <label className="flex items-center gap-2 rounded border border-brand-200 bg-brand-50 px-3 py-2 mb-3">
                    <input
                      type="checkbox"
                      checked={allOn}
                      ref={(el) => { if (el) el.indeterminate = !allOn && !noneOn }}
                      onChange={(e) => {
                        const value = e.target.checked
                        setEditModules((prev) => {
                          const next = { ...prev }
                          for (const m of CUSTOMER_MODULES) next[m.id] = value
                          return next
                        })
                      }}
                    />
                    <div>
                      <div className="text-sm font-semibold">Alle Optionen im Kundenportal</div>
                      <div className="text-xs text-gray-500">Schaltet alle acht Berechtigungen unten gleichzeitig ein oder aus.</div>
                    </div>
                  </label>
                )
              })()}
              <div className="space-y-1">
                {CUSTOMER_MODULES.map((m) => (
                  <label key={m.id} className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={editModules[m.id] ?? true}
                      onChange={(e) => setEditModules((prev) => ({ ...prev, [m.id]: e.target.checked }))}
                    />
                    <div>
                      <div className="font-medium">{m.label}</div>
                      <div className="text-xs text-gray-500">{m.hint}</div>
                    </div>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Default-Deny: jedes Recht muss manuell freigeschaltet werden. Neue
                Kunden-User starten ohne Berechtigungen, und neu eingeführte Module
                werden bestehenden Usern nicht automatisch vergeben — der Admin muss
                jeden Schalter bewusst aktivieren. Deaktivierte Module sind im
                Kundenportal ausgeblendet und die zugehörigen API-Endpoints liefern 403.
              </p>
            </div>

            {/* TANSS-Credentials */}
            <div className="border-t border-gray-200 pt-3">
              <div className="text-sm font-medium text-gray-700 mb-2">TANSS-Credentials</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="TANSS-Benutzername"
                  value={editTanssUser}
                  onChange={(e) => setEditTanssUser(e.target.value)}
                  autoComplete="off"
                />
                <Input
                  label="TANSS-Passwort"
                  type="password"
                  value={editTanssPassword}
                  onChange={(e) => setEditTanssPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder={editTanssHasPassword ? '•••••••• (gesetzt)' : ''}
                  hint={editTanssHasPassword ? 'Passwort gesetzt — leer lassen für unverändert.' : undefined}
                />
                <Input
                  label="2FA-Code (optional)"
                  value={editTanss2faCode}
                  onChange={(e) => setEditTanss2faCode(e.target.value)}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Benutzername und 2FA werden vorausgefüllt. Das Passwort wird aus Sicherheitsgründen nicht angezeigt — leer lassen lässt das gespeicherte Passwort unverändert.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setEditUser(null)}>Abbrechen</Button>
              <Button variant="primary" onClick={saveEdit}>Speichern</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={showCreate}
        onOpenChange={(o) => { if (!o) { setShowCreate(false); resetCreate() } }}
        title="Neuen Kunden-User anlegen"
        size="lg"
      >
        <div className="space-y-4">
          {createError && (
            <div className="p-2 rounded bg-red-50 border border-red-200 text-sm text-red-800">{createError}</div>
          )}

          {/* Schritt 1: Kunde live aus TANSS */}
          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">1. Kunde aus TANSS suchen</div>
            {selectedCompany ? (
              <div className="flex items-center justify-between p-2 rounded border border-gray-200 bg-gray-50">
                <div className="text-sm">
                  <div className="font-medium">{selectedCompany.name}</div>
                  <div className="text-xs text-gray-500">TANSS-ID: {selectedCompany.id}{selectedCompany.email ? ` · ${selectedCompany.email}` : ''}</div>
                </div>
                <Button variant="ghost" size="xs" onClick={() => { setSelectedCompany(null); setTanssEmployees(null); setSelectedEmployee(null) }}>Ändern</Button>
              </div>
            ) : (
              <>
                <Input placeholder="Firma eingeben (min. 2 Zeichen) …" value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} autoFocus />
                <div className="mt-2 border border-gray-200 rounded max-h-48 overflow-auto">
                  {companyFilter.trim().length < 2 && <div className="p-3 text-xs text-gray-500">Min. 2 Zeichen eingeben.</div>}
                  {companyFilter.trim().length >= 2 && companySearching && <div className="p-3 text-xs text-gray-500">Suche …</div>}
                  {companyFilter.trim().length >= 2 && !companySearching && companyResults && companyResults.length === 0 && <div className="p-3 text-xs text-gray-500">Keine Treffer in TANSS.</div>}
                  {companyResults?.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => selectCompany(c)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-gray-500">TANSS-ID: {c.id}{c.email ? ` · ${c.email}` : ''}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Schritt 2: Mitarbeiter */}
          {selectedCompany && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">2. Ansprechpartner/Mitarbeiter (optional)</div>
              {selectedEmployee ? (
                <div className="flex items-center justify-between p-2 rounded border border-gray-200 bg-gray-50">
                  <div className="text-sm">
                    <div className="font-medium">{selectedEmployee.name}</div>
                    <div className="text-xs text-gray-500">TANSS-ID: {selectedEmployee.id}{selectedEmployee.email ? ` · ${selectedEmployee.email}` : ''}</div>
                  </div>
                  <Button variant="ghost" size="xs" onClick={() => setSelectedEmployee(null)}>Ändern</Button>
                </div>
              ) : (
                <>
                  <Input placeholder="Mitarbeiter suchen …" value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)} />
                  <div className="mt-2 border border-gray-200 rounded max-h-48 overflow-auto">
                    {tanssEmployees === null && <div className="p-3 text-xs text-gray-500">Lade TANSS-Mitarbeiter …</div>}
                    {tanssEmployees && filteredEmployees.length === 0 && <div className="p-3 text-xs text-gray-500">Keine Treffer. Du kannst Name/E-Mail manuell unten eintragen.</div>}
                    {filteredEmployees.map((e) => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => selectEmployee(e)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium">{e.name || '(ohne Name)'}</div>
                        <div className="text-xs text-gray-500">TANSS-ID: {e.id}{e.email ? ` · ${e.email}` : ''}</div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Schritt 3: Account-Details */}
          {selectedCompany && (
            <div className="border-t border-gray-200 pt-3">
              <div className="text-sm font-medium text-gray-700 mb-2">3. Account-Details</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input label="Name" value={newName} onChange={(e) => setNewName(e.target.value)} required />
                <Input label="E-Mail (Login)" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required />
              </div>
              <div className="mt-3">
                <Input
                  label="Passwort (optional)"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  hint="Leer lassen → Kunde meldet sich ausschließlich via Microsoft 365 an. Mindestens 8 Zeichen, wenn gesetzt."
                />
              </div>
              <div className="mt-2">
                <Switch label="Sofort aktiv" checked={newActive} onChange={(e) => setNewActive(e.target.checked)} />
              </div>
            </div>
          )}

          {/* Schritt 4: TANSS-Credentials */}
          {selectedCompany && (
            <div className="border-t border-gray-200 pt-3">
              <div className="text-sm font-medium text-gray-700 mb-2">4. TANSS-Credentials (optional, später setzbar)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input label="TANSS-Benutzer" value={newTanssUser} onChange={(e) => setNewTanssUser(e.target.value)} />
                <Input label="TANSS-Passwort" type="password" value={newTanssPassword} onChange={(e) => setNewTanssPassword(e.target.value)} />
                <Input label="2FA-Code (optional)" value={newTanss2fa} onChange={(e) => setNewTanss2fa(e.target.value)} />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => { setShowCreate(false); resetCreate() }}>Abbrechen</Button>
            <Button variant="primary" onClick={createUser} disabled={creating || !selectedCompany}>
              {creating ? 'Lege an …' : 'User anlegen'}
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  )
}
